import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LoadPanel } from "devextreme-react";
import { getProducts } from "../../../api/support";
import { MOCK_PRODUCTS, ProductCard } from "./products-mock";
import "../messages/TicketPage.css";

// Urunler — mobi urun ailesi kart gorunumu. Backend getProducts() bos donerse
// mock fallback'a duser (OrganizationsPage ile ayni desen).
export default function ProductsPage() {
    const { t } = useTranslation();
    const [products, setProducts] = useState<ProductCard[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "passive">("all");

    const load = useCallback(() => {
        setLoading(true);
        getProducts(false)
            .then(list => {
                // Backend Product'inda gorsel yok; gelen kayitlari ada gore mock gorselle eslestir.
                if (list.length) {
                    const byName = new Map(MOCK_PRODUCTS.map(p => [p.name.toLowerCase(), p.image]));
                    setProducts(list.map(p => ({
                        ref: p.ref, code: p.code, name: p.name,
                        description: p.description ?? "",
                        image: byName.get(p.name.toLowerCase()) ?? "",
                        is_active: p.is_active,
                    })));
                } else {
                    setProducts(MOCK_PRODUCTS);
                }
            })
            .catch(() => setProducts(MOCK_PRODUCTS))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = products.filter(p =>
        statusFilter === "all" ? true : statusFilter === "active" ? p.is_active : !p.is_active
    );

    const filters: { key: "all" | "active" | "passive"; label: string }[] = [
        { key: "all", label: t("all") },
        { key: "active", label: t("active") },
        { key: "passive", label: t("passive") },
    ];

    return (
        <div className="ticket-page-root flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <LoadPanel visible={loading} />

            {/* Baslik + durum filtresi */}
            <div className="flex items-center justify-between gap-3 shrink-0" style={{ padding: "16px 20px 12px" }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: "var(--base-text-color)", margin: 0 }}>
                    {t("products")}
                    <span className="text-sm" style={{ marginLeft: 8, color: "var(--subtitle-text-color)", fontWeight: 400 }}>
                        ({filtered.length})
                    </span>
                </h2>
                <div className="flex items-center gap-1">
                    {filters.map(f => (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setStatusFilter(f.key)}
                            className="text-sm"
                            style={{
                                padding: "6px 12px", borderRadius: 8, fontWeight: 600,
                                color: statusFilter === f.key ? "#fff" : "var(--subtitle-text-color)",
                                background: statusFilter === f.key ? "var(--accent-color, #6366F1)" : "transparent",
                                border: statusFilter === f.key ? "1px solid transparent" : "1px solid var(--border-color)",
                            }}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Urun kartlari */}
            <div className="flex-1 min-h-0" style={{ overflowY: "auto", padding: "4px 20px 20px" }}>
                <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
                    {filtered.map(p => (
                        <div
                            key={p.ref}
                            style={{
                                background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 12,
                                padding: 16, display: "flex", flexDirection: "column", gap: 12,
                                opacity: p.is_active ? 1 : 0.6,
                            }}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="flex items-center justify-center shrink-0"
                                    style={{ width: 48, height: 48, borderRadius: 10, background: "var(--background-color)", border: "1px solid var(--border-color)", overflow: "hidden" }}
                                >
                                    {p.image
                                        ? <img src={p.image} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }} />
                                        : <i className="dx-icon-box" style={{ fontSize: 22, color: "var(--accent-color, #6366F1)" }} />}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-semibold truncate" style={{ color: "var(--base-text-color)" }}>{p.name}</div>
                                    <div className="text-xs font-mono" style={{ color: "var(--subtitle-text-color)" }}>#{p.code}</div>
                                </div>
                            </div>

                            <div className="text-xs" style={{ color: "var(--subtitle-text-color)", flex: 1, lineHeight: 1.5 }}>
                                {p.description}
                            </div>

                            <div>
                                <span style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    background: p.is_active ? "#10B9811f" : "#9CA3AF1f",
                                    color: p.is_active ? "#10B981" : "#9CA3AF",
                                    fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 999,
                                }}>
                                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: p.is_active ? "#10B981" : "#9CA3AF" }} />
                                    {p.is_active ? t("active") : t("passive")}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div className="flex items-center justify-center" style={{ padding: 40, color: "var(--subtitle-text-color)" }}>
                        {t("noDataText")}
                    </div>
                )}
            </div>
        </div>
    );
}
